// Signed-in user's profile / account page.
//
// Shows the user's email and verification status, and surfaces the common
// account actions (change password, jump to items, sign
// out). Also exposes the irreversible "delete account" flow via Amplify UI's
// AccountSettings.DeleteUser, which handles its own confirmation dialog.

import { useNavigate, Link } from 'react-router-dom';
import Card from 'react-bootstrap/Card';
import Button from 'react-bootstrap/Button';
import Badge from 'react-bootstrap/Badge';
import Stack from 'react-bootstrap/Stack';
import { AccountSettings } from '@aws-amplify/ui-react';
import * as Urls from '../fleet-shared/Urls.mjs';

// Amplify returns email_verified as either a boolean or the string "true"
// depending on call path (fetchUserAttributes serialises it as a string,
// while Cognito-shaped objects elsewhere use a boolean). Normalise here so
// the badge logic doesn't have to know.
function isEmailVerified(user) {
  const v = user?.email_verified;
  return v === true || v === 'true';
}

const Profile = ({ user, signOut }) => {
  const navigate = useNavigate();

  if (!user) {
    return (
      <div className="container py-4 text-center text-body-secondary">
        Loading profile...
      </div>
    );
  }

  const verified = isEmailVerified(user);

  return (
    <div className="container py-4" style={{ maxWidth: 720 }}>
      <h1 className="mb-4">Profile</h1>

      <Card className="mb-4">
        <Card.Body>
          <Card.Title as="h2" className="h5 mb-3">Account</Card.Title>

          <dl className="row mb-0">
            <dt className="col-sm-4 text-body-secondary">Email</dt>
            <dd className="col-sm-8 d-flex align-items-center gap-2">
              <span>{user.email || <em className="text-body-secondary">unknown</em>}</span>
              {verified ? (
                <Badge bg="success" aria-label="Email verified">Verified</Badge>
              ) : (
                <Badge bg="warning" text="dark" aria-label="Email not verified">Unverified</Badge>
              )}
            </dd>
          </dl>
        </Card.Body>
      </Card>

      <Card className="mb-4">
        <Card.Body>
          <Card.Title as="h2" className="h5 mb-3">Account actions</Card.Title>
          <Stack gap={2}>
            <div>
              <Button
                as={Link}
                to={Urls.urlForChangePassword()}
                variant="outline-primary"
              >
                <i className="fa-solid fa-key me-2" aria-hidden="true"></i>
                Change Password
              </Button>
            </div>
            <div>
              <Button
                as={Link}
                to={Urls.urlForAllItems()}
                variant="outline-secondary"
              >
                <i className="fa-solid fa-list me-2" aria-hidden="true"></i>
                Your items
              </Button>
            </div>
            <div>
              <Button
                id="signOut"
                variant="danger"
                onClick={() => signOut && signOut()}
              >
                <i className="fa-solid fa-right-from-bracket me-2" aria-hidden="true"></i>
                Sign Out
              </Button>
            </div>
          </Stack>
        </Card.Body>
      </Card>

      <Card border="danger" className="mb-4">
        <Card.Body>
          <Card.Title as="h2" className="h5 text-danger mb-2">
            Delete account
          </Card.Title>
          <Card.Text className="text-body-secondary">
            Permanently delete your TaskPogo account. This cannot be undone.
          </Card.Text>
          <AccountSettings.DeleteUser onSuccess={() => navigate('/')} />
        </Card.Body>
      </Card>
    </div>
  );
};

export default Profile;
